'use client';

import { useState } from 'react';
import { useTranslations } from 'next-intl';
import { toast } from 'sonner';
import { useSessionsQuery, useDeleteSessionMutation, useLogoutAllMutation } from '../hooks/queries/useSessions';
import DeleteConfirmModal from './DeleteConfirmModal';
import { Spinner } from './loading/Spinner';

export default function SessionManagement() {
  const t = useTranslations('profile.page');
  const tCommon = useTranslations('common');
  const [deleteTargetSessionId, setDeleteTargetSessionId] = useState<string | null>(null);
  const [isLogoutAllConfirmOpen, setIsLogoutAllConfirmOpen] = useState(false);
  const { data: sessions, isLoading } = useSessionsQuery();
  const deleteSessionMutation = useDeleteSessionMutation();
  const logoutAllMutation = useLogoutAllMutation();

  const handleDeleteSession = async () => {
    if (!deleteTargetSessionId) return;
    try {
      await deleteSessionMutation.mutateAsync(deleteTargetSessionId);
      toast.success(t('sessionsDeleted'));
    } catch {
      toast.error(t('sessionsDeleteFailed'));
    } finally {
      setDeleteTargetSessionId(null);
    }
  };

  const handleLogoutAll = async () => {
    try {
      await logoutAllMutation.mutateAsync();
      toast.success(t('sessionsLogoutAllSuccess'));
    } catch {
      toast.error(t('sessionsLogoutAllFailed'));
    } finally {
      setIsLogoutAllConfirmOpen(false);
    }
  };

  return (
    <div>
      <h4 className="text-sm font-semibold">{t('sessionsTitle')}</h4>
      <p className="mt-1 text-xs text-[var(--color-fg-muted)]">{t('sessionsDescription')}</p>

      <div className="mt-3 space-y-2">
        {isLoading && (
          <div className="flex justify-center py-4">
            <Spinner size="sm" />
          </div>
        )}

        {!isLoading && (!sessions || sessions.length === 0) && (
          <p className="py-2 text-center text-xs text-[var(--color-fg-muted)]">{t('sessionsNoSessions')}</p>
        )}

        {sessions && sessions.length > 0 && (
          <ul className="max-h-80 space-y-1.5 overflow-y-auto">
            {sessions.map((session) => (
              <li
                key={session.id}
                className="flex items-center justify-between rounded-lg border border-base-300 px-3 py-2"
              >
                <div className="flex items-center gap-2 text-xs">
                  <svg
                    className="h-4 w-4 shrink-0 text-[var(--color-fg-muted)]"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <rect x="2" y="3" width="20" height="14" rx="2" />
                    <path d="M8 21h8M12 17v4" />
                  </svg>
                  <span className="text-base-content/80">
                    {session.kind === 'DESKTOP'
                      ? t('sessionsDesktopDevice')
                      : (session.deviceInfo ?? t('sessionsUnknownDevice'))}
                  </span>
                  {session.isCurrent && (
                    <span className="rounded bg-primary/10 px-1.5 py-0.5 text-[10px] font-medium text-primary">
                      {t('sessionsCurrentDevice')}
                    </span>
                  )}
                </div>
                {!session.isCurrent && (
                  <button
                    type="button"
                    className="btn btn-ghost btn-xs text-error/70 hover:text-error"
                    onClick={() => setDeleteTargetSessionId(session.id)}
                    disabled={deleteSessionMutation.isPending}
                    aria-label={t('sessionsDeleteConfirmLabel')}
                  >
                    <svg
                      className="h-3.5 w-3.5"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}

        {sessions && sessions.length > 1 && (
          <button
            type="button"
            className="btn btn-ghost btn-sm w-full text-xs text-error/70 hover:text-error"
            onClick={() => setIsLogoutAllConfirmOpen(true)}
            disabled={logoutAllMutation.isPending}
          >
            {logoutAllMutation.isPending ? <Spinner /> : t('sessionsLogoutAll')}
          </button>
        )}
      </div>

      <DeleteConfirmModal
        isOpen={deleteTargetSessionId !== null}
        title={t('sessionsDeleteConfirm')}
        message={t('sessionsDeleteConfirmMessage')}
        confirmLabel={t('sessionsDeleteConfirmLabel')}
        cancelLabel={tCommon('actions.cancel')}
        isPending={deleteSessionMutation.isPending}
        onConfirm={() => void handleDeleteSession()}
        onCancel={() => setDeleteTargetSessionId(null)}
      />

      <DeleteConfirmModal
        isOpen={isLogoutAllConfirmOpen}
        title={t('sessionsLogoutAllConfirm')}
        message={t('sessionsLogoutAllConfirmMessage')}
        confirmLabel={t('sessionsLogoutAll')}
        cancelLabel={tCommon('actions.cancel')}
        isPending={logoutAllMutation.isPending}
        onConfirm={() => void handleLogoutAll()}
        onCancel={() => setIsLogoutAllConfirmOpen(false)}
      />
    </div>
  );
}
